import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, input, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { DocumentTemplateWithPath } from '../../../../types/document-template';

/** Gap kept between the pointer and the chip when it flips to the other side (keep in sync with `.is-flipped`). */
const FLIP_GAP_PX = 12;

/**
 * Cursor-following chip rendered as the `cdkDragPreview` of a template card.
 *
 * The cards on the Templates page live inside the zoomable anchor-tree canvas,
 * where CDK sizes a cloned preview to the card's *scaled* rect while its
 * content still renders at 1:1 — the card ends up clipped mid-content. A
 * dedicated, zoom-independent chip keeps the drag readable at any zoom level
 * and leaves the tree visible underneath.
 */
@Component({
  selector: 'app-template-drag-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
  styles: [`
    /* The preview host is a popover; its UA overflow rule would clip the chip
       once it flips to the other side of the pointer. */
    :host { display: block; overflow: visible; }
    @keyframes template-drag-chip-in {
      from { opacity: 0; transform: scale(0.96); }
      to { opacity: 1; transform: scale(1); }
    }
    .drag-chip {
      transform-origin: top left;
      animation: template-drag-chip-in 150ms ease-out;
    }
    .drag-chip-shift { transition: transform 150ms ease-out; }
    .drag-chip-shift.is-flipped { transform: translateX(calc(-100% - 12px)); }
    @media (prefers-reduced-motion: reduce) {
      .drag-chip { animation: none; }
      .drag-chip-shift { transition: none; }
    }
  `],
  template: `
    <div class="drag-chip-shift" [class.is-flipped]="isFlipped()">
      <div
        data-testid="template-drag-preview"
        class="drag-chip pointer-events-none flex max-w-[22rem] items-center gap-2.5 rounded-lg border border-indigo-400 dark:border-indigo-500 bg-white dark:bg-zinc-800 px-3 py-2 shadow-xl ring-4 ring-indigo-500/15"
      >
        <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300">
          <mat-icon class="!text-base !w-4 !h-4 !leading-4" aria-hidden="true">description</mat-icon>
        </span>
        <span class="min-w-0">
          <span class="block truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{{ template().name }}</span>
          <span class="block truncate text-[11px] text-zinc-500 dark:text-zinc-400">{{ hint() }}</span>
        </span>
      </div>
    </div>
  `
})
export class TemplateDragPreviewComponent {
  template = input.required<DocumentTemplateWithPath>();
  hint = input('Drop on a leaf to assign');

  /**
   * CDK anchors the preview's top-left corner to the pointer, which pushes the
   * chip off-screen once the drop target sits near the right edge (the unplaced
   * panel). Mirror it to the other side of the pointer before that happens.
   */
  protected readonly isFlipped = signal(false);

  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private width = 0;

  constructor() {
    const document = inject(DOCUMENT);
    const onPointerMove = (event: PointerEvent) => {
      this.width ||= this.host.offsetWidth;
      const viewportWidth = document.defaultView?.innerWidth ?? 0;
      this.isFlipped.set(event.clientX + this.width + FLIP_GAP_PX > viewportWidth);
    };
    document.addEventListener('pointermove', onPointerMove, { passive: true });
    inject(DestroyRef).onDestroy(() => document.removeEventListener('pointermove', onPointerMove));
  }
}
